import { useState, useEffect, createContext } from 'react'
import { login, register, forgotPassword } from '../api/auth'
import { toast } from 'sonner'
import api from '../api/axios'
import i18n from '../i18n'

const isPt = () => (i18n.language || 'pt').startsWith('pt')

export const AuthContext = createContext({})


function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loadingAuth, setLoadingAuth] = useState(false);
    const [load, setLoad] = useState(true);

    useEffect(() => {
        // Tenta restaurar primeiro de localStorage; em fallback, sessionStorage (lembrar-me=false).
        const persisted = localStorage.getItem('SistemaUser') || sessionStorage.getItem('SistemaUser');

        if (persisted) {
            setUser(JSON.parse(persisted));
        }

        setLoad(false);

    }, [])

    // Sincroniza idioma quando user logado tem language preferida
    useEffect(() => {
        const i18n = require('../i18n').default;
        if (user?.language && i18n.language !== user.language) {
            i18n.changeLanguage(user.language);
        }
    }, [user?.language]);

    async function signUp(email, nome, password, options = {}) {
        const { silentToast = false, throwOnError = false } = options;
        setLoadingAuth(true)
        try {
            const { user, token } = await register(nome, email, password)

            setUser(user)
            storageUser(user, token)

            if (!silentToast) {
                toast.success(isPt() ? 'Conta criada com sucesso!' : 'Account created!')
            }
            return { user, token }
        } catch (err) {
            console.error(err)
            setUser(null)

            localStorage.removeItem('SistemaUser')
            localStorage.removeItem('token')
            delete api.defaults.headers.common['Authorization']

            const errorMessage = err.response?.data?.error || err.response?.data?.message || (isPt() ? 'Erro no cadastro. Tente novamente.' : 'Sign up failed. Try again.')
            if (!silentToast) toast.error(errorMessage)
            if (throwOnError) throw err
        } finally {
            setLoadingAuth(false)
        }
    }

    function storageUser(user, token, remember = true) {
        const target = remember ? localStorage : sessionStorage;
        const opposite = remember ? sessionStorage : localStorage;
        target.setItem('SistemaUser', JSON.stringify(user))
        target.setItem('token', token)
        // Garante que a outra storage não fique com sessão duplicada/órfã
        opposite.removeItem('SistemaUser')
        opposite.removeItem('token')
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`
    }

    function signOut() {
        setUser(null);

        localStorage.removeItem('SistemaUser');
        localStorage.removeItem('token');
        sessionStorage.removeItem('SistemaUser');
        sessionStorage.removeItem('token');

        delete api.defaults.headers.common['Authorization'];

        toast.success(isPt() ? 'Desconectado' : 'Signed out');
    }

    async function signIn(email, password, remember = true) {
        setLoadingAuth(true)
        try {
            const { user, token } = await login(email, password)
            setUser(user)
            storageUser(user, token, remember)

            toast.success(isPt() ? 'Bem vindo de volta' : 'Welcome back')
        } catch (err) {
            console.error(err)
            // Garante que o usuário não seja setado em caso de erro
            setUser(null)
            
            // Limpa dados de autenticação em caso de erro
            localStorage.removeItem('SistemaUser')
            localStorage.removeItem('token')
            delete api.defaults.headers.common['Authorization']
            
            // Captura a mensagem de erro da API
            const errorMessage = err.response?.data?.error || err.response?.data?.message || (isPt() ? 'Usuário ou senha inválido' : 'Invalid email or password')
            toast.error(errorMessage)
        } finally {
            setLoadingAuth(false)
        }
    }

    async function forgot(email) {
        setLoadingAuth(true)
        try {
            const response = await forgotPassword(email)
            toast.success(response.message || (isPt()
                ? 'Se o email existir em nossa base, você receberá instruções por e-mail.'
                : 'If the email exists in our base, you will receive recovery instructions.'))
            return response
        } catch (err) {
            console.error(err)
            const errorMessage = err.response?.data?.error || (isPt() ? 'Erro ao recuperar senha. Tente novamente.' : 'Failed to recover password. Try again.')
            toast.error(errorMessage)
            throw err
        } finally {
            setLoadingAuth(false)
        }
    }

    return (
        <AuthContext.Provider value={{
            signed: !!user,
            user,
            load,
            signUp,
            signOut,
            signIn,
            forgot,
            loadingAuth,
            setUser,
            storageUser
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export default AuthProvider;